<script module lang="ts">
	export enum ToastType {
		INFO = 'Info',
		SUCCESS = 'Success!',
		WARNING = 'Warning',
		ERROR = 'Error'
	}

	const TOAST_THEMES = {
		[ToastType.SUCCESS]: { variant: 'success', defaultIcon: 'checkCircle' },
		[ToastType.ERROR]: { variant: 'danger', defaultIcon: 'fieldError' },
		[ToastType.WARNING]: { variant: 'warning', defaultIcon: 'error' },
		[ToastType.INFO]: { variant: 'info', defaultIcon: 'info' }
	} as const
</script>

<script lang="ts">
	import { Button, ButtonType, ButtonSize, Icon } from '@repo/commons/components'
	import { fadeSlide } from '@repo/commons/utils'
	import type { IconName } from '$lib_commons/svg-icons/iconsMap.ts'

	interface Props {
		toastType?: ToastType
		compact?: boolean
		dismissible?: boolean
		onDismiss?: (() => void) | null
		heading?: import('svelte').Snippet
		text?: import('svelte').Snippet
		fullWidth?: boolean
		actionLabel?: string
		toastIcon?: IconName
		onAction?: (() => void) | null
	}

	let {
		toastType = ToastType.INFO,
		compact = false,
		dismissible = true,
		onDismiss = null,
		heading,
		text,
		toastIcon,
		actionLabel,
		onAction = null,
		fullWidth = false
	}: Props = $props()

	const iconProps = $derived.by(() => {
		const theme = TOAST_THEMES[toastType] || TOAST_THEMES[ToastType.INFO]

		return {
			size: 'md',
			variant: theme.variant,
			name: toastIcon || theme.defaultIcon
		}
	})
</script>

<div
	class="toast-base"
	class:toast-compact={compact}
	class:toast-full-width={fullWidth}
	class:toast-with-action={!!actionLabel}
	class:toast-error={toastType === ToastType.ERROR}
	class:toast-warning={toastType === ToastType.WARNING}
	class:toast-success={toastType === ToastType.SUCCESS}
	class:toast-info={toastType === ToastType.INFO}
	in:fadeSlide={{ duration: 300, x: -80, delay: 200 }}
	out:fadeSlide={{ duration: 200, x: -80 }}
	role="alert"
>
	<div class="toast-border"></div>
	<div class="toast-content">
		<div class="toast-icon">
			<div class="icon-wrapper">
				<Icon name={iconProps.name} size={iconProps.size} variant={iconProps.variant} />
			</div>
		</div>
		<div class="toast-text">
			{#if heading}
				<div class="toast-heading">
					{@render heading()}
				</div>
			{/if}
			<div class="toast-description">
				{#if text}
					{@render text()}
				{/if}
			</div>
			{#if actionLabel}
				<div class="toast-action">
					<Button
						buttonText={actionLabel}
						buttonType={ButtonType.LINK}
						buttonSize={ButtonSize.MEDIUM}
						onclick={() => onAction?.()}
					/>
				</div>
			{/if}
		</div>
	</div>
	{#if dismissible}
		<div class="toast-close">
			<Button
				buttonText=""
				buttonType={ButtonType.TERTIARY_INVERSE}
				buttonSize={ButtonSize.SMALL}
				rounded
				iconProps={{ name: 'close', size: 'md', class: 'text-[#002C4D80]' }}
				onclick={() => onDismiss?.()}
			/>
		</div>
	{/if}
</div>

<style lang="postcss">
	.toast-error {
		@apply bg-[#fdf6f7] border border-[#E4485B24];
	}
	.toast-warning {
		@apply bg-[#fffcf4] border border-[#8F6C1A33];
	}
	.toast-success {
		@apply bg-[#F6FCFB] border border-[#067A5733]/10;
	}
	.toast-info {
		@apply bg-[#f4fbff] border border-[#1A74A833];
	}
	.toast-base {
		@apply w-auto min-w-[100px] max-w-[600px] h-auto rounded-lg justify-start items-start inline-flex overflow-hidden;
	}
	.toast-full-width {
		display: flex;
		max-width: none;
		width: min(calc(100vw - 2rem), 600px);
	}
	.toast-content {
		@apply grow shrink basis-0 p-5 justify-start items-start gap-3 flex;
	}
	.toast-compact .toast-content {
		@apply justify-center items-center;
	}
	.toast-base.toast-compact.toast-with-action .toast-content {
		@apply items-start;
	}
	.toast-border {
		@apply w-1 self-stretch;
	}
	.toast-icon {
		@apply w-6 h-7 justify-center items-center gap-2 flex;
	}
	.icon-wrapper {
		@apply w-6 h-6 flex-col justify-center items-center gap-2.5 inline-flex overflow-hidden;
	}
	.toast-text {
		@apply grow shrink basis-0 flex-col justify-start items-start gap-1 flex min-w-0;
	}
	.toast-compact .toast-text {
		@apply gap-0;
		line-height: 2;
	}
	.toast-heading {
		@apply self-stretch text-[#001626E5] text-xl font-semibold leading-7;
	}
	.toast-description {
		@apply text-[#001D33B2] text-base self-stretch flex-col justify-start items-start gap-4 flex min-w-0;
		overflow-wrap: break-word;
		word-break: break-word;
	}
	.toast-action {
		@apply mt-2;
	}
	.toast-close {
		@apply flex-col justify-center items-start gap-2.5 inline-flex overflow-hidden p-5;
	}

	.toast-error .toast-border {
		@apply bg-[#E4485BCC];
	}
	.toast-warning .toast-border {
		@apply bg-[#8F6C1ACC];
	}
	.toast-success .toast-border {
		@apply bg-[#067A57CC];
	}
	.toast-info .toast-border {
		@apply bg-[#1A74A8CC];
	}
</style>
